import { ApiErrorResponse, SortDirection, SortQueryParam } from "@/types/apis";
import { AxiosError } from "axios";
import { clsx, type ClassValue } from "clsx";
import { parseISO } from "date-fns";
import { toast } from "sonner";
import { twMerge } from "tailwind-merge";

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}

export const getMessageFromError = (
  error: Error,
  fallbackMessage: string = "Ha ocurrido un error general.",
) => {
  const axiosError = error as AxiosError<ApiErrorResponse>;

  if (axiosError.code === "ERR_NETWORK") {
    return "No hay conexión con el servidor de datos. Inténtalo más tarde.";
  }

  return (
    axiosError.response?.data?.detail ||
    axiosError.response?.data?.message ||
    axiosError.message ||
    fallbackMessage
  );
};

export const apiErrorToToast = (
  axiosError: AxiosError<ApiErrorResponse>,
  title: string = "",
  description: string = "",
) => {
  toast.error(title || "Error", {
    description: description || getMessageFromError(axiosError),
  });
};

/**
 * Converts a UTC date string from the server to a JavaScript Date object.
 *
 * The server date string is assumed to be in UTC but does not explicitly include the time zone.
 * This function appends 'Z' to indicate UTC and ensures correct parsing.
 *
 * @param dateString - The date string from the server (e.g., "2024-10-17T22:20:20.367372").
 * @returns A JavaScript Date object representing the parsed UTC date and time.
 */
export const dateFromServer = (dateString: string): Date => {
  // Trim the date string to include only up to seconds and append 'Z' to denote UTC
  const utcDateString = dateString.slice(0, 19) + "Z";
  return parseISO(utcDateString);
};

export const renderHtmlChoiceId = (choiceId: string) =>
  `choice-${choiceId.replace(/\./g, "_")}`;

export const renderMarkdown = (markdown: string) => {
  return (
    markdown
      // Replace new lines with <br>
      .replace(/\n/g, "<br>")
      // Bold
      .replace(/\*(.*?)\*/g, "<strong>$1</strong>")
      // Italic
      .replace(/_(.*?)_/g, "<em>$1</em>")
  );
};

export const formatLessThan10 = (n: number): string => {
  if (n === 0) return "0";
  if (n < 10) {
    return `0${n}`;
  }
  return `${n}`;
};

export const capitalizeFirstLetter = (str: string): string => {
  return str.charAt(0).toUpperCase() + str.slice(1);
};

export const validationEmail = (email: string): boolean => {
  const re =
    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/;
  return re.test(email);
};

export const paginationSizes = [5, 10, 15, 20, 25];

export const forceFileDownload = (url: string, fileName?: string) => {
  const link = document.createElement("a");
  link.href = url;
  link.target = "_blank";
  link.rel = "noopener noreferrer";
  link.setAttribute("download", fileName || url);

  // Start download
  link.click();
};

export const getResourceDownloadUrl = (
  resourceLibraryId: string,
  resourceId: string,
) => {
  if (!(resourceLibraryId && resourceId)) {
    return;
  }

  return `${import.meta.env.VITE_API_URL}/v1/resource-libraries/${resourceLibraryId}/resources/${resourceId}/download`;
};

export const serializeSortQueryParam = (
  criteria: SortQueryParam[] | undefined,
): string[] => {
  if (!Array.isArray(criteria)) {
    return [];
  }

  return criteria.map((c) => `${c.field},${c.direction}`);
};

export const deserializeSortQueryParam = (sort: string): SortQueryParam[] => {
  if (!sort) {
    return [];
  }

  return sort
    .split(",")
    .map((s) => s.split(","))
    .map(([field, direction]) => ({
      field,
      direction: direction as SortDirection,
    }));
};
